import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useTicketsStore } from 'pages/ticketV2/store/ticketsStore';
import {
  RedLinkButton,
  SdmTextfieldV5,
  getProductDescription,
  getProductName,
} from 'pages/ticketV2/common';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import {
  discountCodeId,
  discountCodeStatus,
  isDiscountTicket,
  Roles,
} from 'utils/constants';
import DiscountBox from './DiscountBox';
import { useNetwork } from 'services/network';
import { LoaderCountContext } from 'context/LoaderV5';

const currencyFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
});

const TicketsContainer = ({ fetchedProducts, locale, loggedUser }) => {
  const theme = useTheme();
  const network = useNetwork();
  const { setLoaderActive } = useContext(LoaderCountContext);
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const intl = useIntl();
  const i18nCommon = translations.TicketsV2(intl).Common;
  const i18nDiscount = translations.DiscountCode(intl);
  const isOperator = loggedUser.type === Roles.OPERATOR;
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isIphone5 = useMediaQuery(theme.breakpoints.down(376));

  const {
    productsInCart,
    ticketsData,
    isCartFull,
    submittedDiscountCodesWithEmptySpaces,
  } = useTicketsStore();
  const [ticketsQuantity, setTicketsQuantity] = useState({});
  const [expandedCardsIdx, setExpandedCardsIdx] = useState([]);
  const [showGreyBox, setShowGreyBox] = useState(false);
  const [showDiscountTicket, setShowDiscountTicket] = useState(false);
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [discountCodeTxt, setDiscountCodeTxt] = useState('');
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (fetchedProducts?.length) {
      const quantities = {};
      fetchedProducts.map((t) => {
        const currentProdQty =
          productsInCart?.find((p) => p.id === t.id)?.quantity || 0;
        quantities[t.id] = currentProdQty;
      });
      if (
        productsInCart.find(
          (prd) => prd.code === discountCodeId && prd.quantity !== 0
        )
      )
        setShowGreyBox(false);
      setTicketsQuantity(quantities);
      if (fetchedProducts?.find((prod) => !!isDiscountTicket(prod))) {
        const discountCodeId = fetchedProducts.find(
          (prod) => !!isDiscountTicket(prod)
        ).id;
        if (quantities[discountCodeId] === 0 && !showGreyBox) {
          setShowGreyBox(true);
        }
      }
    }
  }, [fetchedProducts]);

  useEffect(() => {
    if (showDiscountTicket && showGreyBox) {
      setShowGreyBox(false);
    }
    if (!showDiscountTicket) {
      setShowDiscountInput(false);
    }
  }, [showDiscountTicket]);

  useEffect(() => {
    if (Object.keys(ticketsQuantity)?.length > 0) {
      let nextSubmittedCodes = [];
      const submittedDiscountCodes =
        submittedDiscountCodesWithEmptySpaces.filter((code) => !!code);
      const _cart = fetchedProducts.map((pro) => {
        if (pro.code !== discountCodeId) {
          Array.from({ length: ticketsQuantity[pro.id] }, (_, i) => i).map(() =>
            nextSubmittedCodes.push('')
          );
        } else {
          nextSubmittedCodes = [
            ...nextSubmittedCodes,
            ...submittedDiscountCodes,
          ];
        }
        return {
          ...pro,
          quantity: ticketsQuantity[pro.id],
        };
      });
      useTicketsStore.setState({
        productsInCart: _cart,
        submittedDiscountCodesWithEmptySpaces: nextSubmittedCodes,
      });
      if (
        _cart.find((prd) => prd.code === discountCodeId && prd.quantity > 0) &&
        !showDiscountTicket
      ) {
        setShowDiscountTicket(true);
        setShowDiscountInput(true);
        setDiscountCodeTxt('');
        setErrMsg('');
      }
    }
  }, [ticketsQuantity]);

  const closeAlert = (sev) =>
    useTicketsStore.setState({ alertData: { severity: sev } });

  const onAddClick = (id) => {
    if (ticketsQuantity[id] < 99)
      setTicketsQuantity((prev) => ({
        ...prev,
        [id]: prev[id] + 1,
      }));
  };

  const onRemoveClick = (id) => {
    setTicketsQuantity((prev) => ({ ...prev, [id]: prev[id] - 1 }));
  };

  const toggleDetails = (idx) => {
    const isExpanded = expandedCardsIdx.find((index) => index === idx);
    if (isExpanded) {
      setExpandedCardsIdx(expandedCardsIdx.filter((index) => index !== idx));
    } else {
      setExpandedCardsIdx((prev) => [...prev, idx]);
    }
  };
  const handleValidCode = (productId) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    // Salvare il codice in una lista per le chips,
    useTicketsStore.setState((prev) => ({
      ...prev,
      submittedDiscountCodesWithEmptySpaces: [
        ...prev.submittedDiscountCodesWithEmptySpaces,
        discountCodeTxt,
      ],
    }));
    useTicketsStore.setState({
      alertData: {
        title: i18nCommon.discountApply,
        onClose: () => closeAlert('success'),
        severity: 'success',
      },
    });
    // Aggiungere al carrello
    onAddClick(productId);
    setDiscountCodeTxt('');
    if (errMsg) setErrMsg('');
    setShowDiscountInput(false);
  };

  const onCodeDelete = ({ code, productId }) => {
    const nextSubmitted = [...submittedDiscountCodesWithEmptySpaces];
    const idx = submittedDiscountCodesWithEmptySpaces.indexOf(code);
    nextSubmitted.splice(idx, 1);
    useTicketsStore.setState({
      submittedDiscountCodesWithEmptySpaces: nextSubmitted,
    });
    onRemoveClick(productId);
    setShowDiscountInput(true);
  };

  const onApplyCode = async (id) => {
    showLoader();
    if (submittedDiscountCodesWithEmptySpaces.includes(discountCodeTxt)) {
      setErrMsg(i18nDiscount.Common.expiredGeneralCode);
      return hideLoader();
    }
    const res = await network.checkCodeLambda({
      code: discountCodeTxt,
      year: `${new Date().getFullYear()}`,
    });
    if (res.status) {
      switch (res.status) {
        case discountCodeStatus.CODE_EXPIRED:
          setErrMsg(i18nDiscount.Common.expiredGeneralCode);
          break;
        case discountCodeStatus.CODE_NOT_FOUND:
          setErrMsg(i18nDiscount.Common.notValidGeneralCode);
          break;
        default:
          handleValidCode(id);
          break;
      }
    } else {
      setErrMsg(i18nDiscount.Common.generalCodeError);
    }
    hideLoader();
  };

  return (
    <Box
      sx={{
        width: isLgDown ? '100%' : '64%',
        ...(!isLgDown && {
          pr: '70px',
        }),
      }}
    >
      <Stack sx={{ mt: '25px' }} direction={'column'} gap={'25px'}>
        {fetchedProducts
          .filter((product) =>
            showDiscountTicket ? product : !isDiscountTicket(product)
          )
          .map((product, idx) => {
            const isLegacyTicket =
              product.capacity === 6 && !isDiscountTicket(product);
            return (
              <Stack
                key={product.id}
                sx={{
                  p: '25px 20px',
                  bgcolor: 'white',
                  border: `2px solid ${
                    ticketsQuantity[product.id] === 0
                      ? theme.palette.grey[200]
                      : theme.palette.primary.main
                  }`,
                }}
              >
                {isLgDown ? (
                  <>
                    {isOperator && !isLegacyTicket ? (
                      <div
                        style={{
                          backgroundColor: theme.palette.grey[200],
                          color: 'black',
                          borderRadius: '25px',
                          width: 'fit-content',
                          display: 'flex',
                          padding: '5px 10px',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '16px',
                          minWidth: '88px',
                        }}
                      >
                        <Typography
                          variant="xs-bold"
                          textAlign="center"
                          fontFamily={'Suisse-Intl'}
                          style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                          component={'p'}
                        >
                          {i18nCommon.novelty} {new Date().getFullYear()}
                        </Typography>
                      </div>
                    ) : null}
                    <Stack
                      direction={'row'}
                      justifyContent={'space-between'}
                      alignItems={'flex-start'}
                      sx={{
                        mb: expandedCardsIdx.find((index) => index === idx + 1)
                          ? '15px'
                          : '30px',
                      }}
                    >
                      <Typography
                        variant="m-bold"
                        sx={{ mb: '5px' }}
                        fontFamily={'Suisse-Intl'}
                        component={'p'}
                      >
                        {getProductName(product, locale)}
                      </Typography>
                      <RedLinkButton
                        onClick={() => toggleDetails(idx + 1)}
                        msg={i18nCommon.details}
                        icon={
                          expandedCardsIdx.find(
                            (index) => index === idx + 1
                          ) ? (
                            <ExpandLessIcon color="primary" />
                          ) : (
                            <ExpandMoreIcon color="primary" />
                          )
                        }
                      />
                    </Stack>
                  </>
                ) : (
                  <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'flex-start'}
                  >
                    <Typography
                      variant="m-bold"
                      fontFamily={'Suisse-Intl'}
                      component={'p'}
                    >
                      {getProductName(product, locale)}
                    </Typography>
                    {isOperator && !isLegacyTicket ? (
                      <div
                        style={{
                          backgroundColor: theme.palette.grey[200],
                          color: 'black',
                          borderRadius: '25px',
                          width: 'fit-content',
                          display: 'flex',
                          padding: '5px 10px',
                          alignItems: 'center',
                          gap: '10px',
                          minWidth: '88px',
                        }}
                      >
                        <Typography
                          variant="xs-bold"
                          textAlign="center"
                          fontFamily={'Suisse-Intl'}
                          component={'p'}
                          style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                        >
                          {i18nCommon.novelty} {new Date().getFullYear()}
                        </Typography>
                      </div>
                    ) : null}
                  </Stack>
                )}
                {!isLgDown ||
                expandedCardsIdx.find((index) => index === idx + 1) ? (
                  <Typography
                    variant="body2"
                    color={'#333333'}
                    sx={{ mb: '15px' }}
                    component={'p'}
                  >
                    {getProductDescription(product, locale)}
                  </Typography>
                ) : null}
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <Typography
                    variant="body2-bold"
                    fontFamily={'Suisse-Intl'}
                    component={'p'}
                    color={'#333333'}
                  >
                    {product?.current_price
                      ? currencyFormatter.format(product?.current_price)
                      : null}
                  </Typography>
                  {isDiscountTicket(product) ? (
                    <></>
                  ) : (
                    <Stack direction={'row'}>
                      <Button
                        aria-label="Decrease quantity"
                        disabled={
                          ticketsQuantity[product.id] === 0 ||
                          ticketsData.filter(
                            (t) =>
                              t.ticketData.id === product.id && t.assignData
                          ).length >= ticketsQuantity[product.id]
                        }
                        variant="contained"
                        sx={{
                          width: '45px',
                          height: '45px',
                          padding: 0,
                          minWidth: 0,
                        }}
                        onClick={() => onRemoveClick(product.id)}
                      >
                        <RemoveIcon fontSize="20px" />
                      </Button>
                      <Stack
                        alignItems={'center'}
                        justifyContent={'center'}
                        sx={{ width: '40px' }}
                      >
                        <Typography
                          variant="body2-bold"
                          fontFamily={'Suisse-Intl'}
                          component={'p'}
                        >
                          {ticketsQuantity[product.id]}
                        </Typography>
                      </Stack>
                      <Button
                        aria-label="Increase quantity"
                        disabled={
                          isCartFull || ticketsQuantity[product.id] === 99
                        }
                        variant="contained"
                        sx={{
                          width: '45px',
                          height: '45px',
                          padding: 0,
                          minWidth: 0,
                        }}
                        onClick={() => onAddClick(product.id)}
                      >
                        <AddIcon fontSize="20px" />
                      </Button>
                    </Stack>
                  )}
                </Stack>
                {isDiscountTicket(product) ? (
                  <>
                    {submittedDiscountCodesWithEmptySpaces?.filter(
                      (code) => !!code
                    ).length ? (
                      <Stack
                        flexDirection={'row'}
                        flexWrap={'wrap'}
                        justifyContent={'flex-start'}
                        gap={'8px'}
                        marginTop={'20px'}
                        alignItems={'center'}
                      >
                        <Typography variant="body2" component={'p'}>
                          {i18nDiscount.Buy.appliedCodes}:{' '}
                        </Typography>
                        {submittedDiscountCodesWithEmptySpaces
                          .filter((code) => !!code)
                          .map((code, idx) => (
                            <Chip
                              key={idx}
                              label={code}
                              variant="outlined"
                              onDelete={() =>
                                onCodeDelete({
                                  code,
                                  productId: product.id,
                                })
                              }
                              style={{
                                maxWidth: 'fit-content',
                                margin: 0,
                                backgroundColor: theme.palette.grey[200],
                                color: 'black',
                                borderRadius: '25px',
                                width: 'fit-content',
                                display: 'flex',
                                padding: '5px',
                                alignItems: 'center',
                                fontWeight: '700',
                                fontSize: '16px',
                              }}
                            />
                          ))}
                      </Stack>
                    ) : (
                      <></>
                    )}
                    {submittedDiscountCodesWithEmptySpaces?.filter(
                      (code) => !!code
                    ).length && !showDiscountInput ? (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => setShowDiscountInput(true)}
                        sx={{
                          fontSize: '16px',
                          lineHeight: '24px',
                          marginTop: '20px',
                          width: 'fit-content',
                        }}
                      >
                        {i18nDiscount.Buy.buttons.add}
                      </Button>
                    ) : (
                      <></>
                    )}
                    {showDiscountInput ? (
                      <>
                        <Stack
                          flexDirection={isSmDown ? 'column' : 'row'}
                          gap={isSmDown ? '8px' : '20px'}
                          justifyContent={'flex-start'}
                          alignItems={isSmDown ? 'flex-start' : 'flex-end'}
                          maxWidth={'487px'}
                          marginTop={'20px'}
                        >
                          <SdmTextfieldV5
                            variant="standard"
                            onChange={(e) => {
                              if (errMsg) setErrMsg('');
                              setDiscountCodeTxt(e.target.value);
                            }}
                            value={discountCodeTxt}
                            isIphone5={isIphone5}
                            label={`${i18nDiscount.Buy.addCodePlaceholder}*`}
                            InputProps={{
                              ...(errMsg && {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <ErrorOutlineOutlinedIcon color="primary" />
                                  </InputAdornment>
                                ),
                              }),
                            }}
                          />
                          {isSmDown ? (
                            <>
                              {errMsg ? (
                                <Typography
                                  variant={'xs-bold'}
                                  color="primary"
                                  fontFamily={'Suisse-Intl'}
                                  component={'p'}
                                >
                                  {errMsg}
                                </Typography>
                              ) : (
                                <Typography
                                  variant={'xs'}
                                  fontFamily={'Suisse-Intl'}
                                  color={'#333333'}
                                >
                                  {i18nDiscount.Buy.addCodeSub}
                                </Typography>
                              )}
                            </>
                          ) : (
                            <></>
                          )}
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            disabled={
                              isCartFull || ticketsQuantity[product.id] === 99
                            }
                            onClick={() => onApplyCode(product.id)}
                            style={{
                              width: 'fit-content',
                              border: '1px solid #EC0E00',
                            }}
                          >
                            {i18nDiscount.Buy.buttons.apply}
                          </Button>
                        </Stack>
                        {!isSmDown ? (
                          <>
                            {errMsg ? (
                              <Typography
                                variant={'xs-bold'}
                                color="primary"
                                style={{ marginTop: '8px' }}
                                fontFamily={'Suisse-Intl'}
                                component={'p'}
                              >
                                {errMsg}
                              </Typography>
                            ) : (
                              <Typography
                                variant={'xs'}
                                style={{ marginTop: '8px' }}
                                fontFamily={'Suisse-Intl'}
                                color={'#333333'}
                                component={'p'}
                              >
                                {i18nDiscount.Buy.addCodeSub}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <></>
                        )}
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </Stack>
            );
          })}
        {showGreyBox ? (
          <DiscountBox
            onAddDiscountTicketClick={() => {
              setShowDiscountTicket(true);
              setShowDiscountInput(true);
            }}
          />
        ) : (
          <></>
        )}
      </Stack>
    </Box>
  );
};
export default TicketsContainer;
